import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Film, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOVIE_GENRES = [
  "Action", "Comedy", "Drama", "Horror", "Sci-Fi", "Romance", 
  "Thriller", "Animation", "Documentary", "Adventure", "Fantasy"
];

const MOVIE_RATINGS = ["G", "PG", "PG-13", "R", "NC-17"];

export default function Movies() {
  const { user, getAuthHeader } = useAuth();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    genre: "",
    duration_minutes: 120,
    rating: "PG",
    description: "",
    poster_url: "",
    logo_url: "",
    showtimes: [],
    is_active: true
  });
  const [newShowtime, setNewShowtime] = useState("");

  const canEdit = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  useEffect(() => {
    fetchMovies();
  }, []);

  const fetchMovies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/movies`, {
        headers: getAuthHeader()
      });
      setMovies(response.data);
    } catch (error) {
      toast.error("Failed to fetch movies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMovie) {
        await axios.put(`${API_URL}/api/movies/${editingMovie.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Movie updated successfully");
      } else {
        await axios.post(`${API_URL}/api/movies`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Movie created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchMovies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (movieId) => {
    if (!window.confirm("Are you sure you want to delete this movie?")) return;
    try {
      await axios.delete(`${API_URL}/api/movies/${movieId}`, {
        headers: getAuthHeader()
      });
      toast.success("Movie deleted successfully");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(movieId);
        return next;
      });
      fetchMovies();
    } catch (error) {
      toast.error("Failed to delete movie");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      genre: "",
      duration_minutes: 120,
      rating: "PG",
      description: "",
      poster_url: "",
      logo_url: "",
      showtimes: [],
      is_active: true
    });
    setEditingMovie(null);
    setNewShowtime("");
  };

  const openEditDialog = (movie) => {
    setEditingMovie(movie);
    setFormData({
      title: movie.title,
      genre: movie.genre,
      duration_minutes: movie.duration_minutes,
      rating: movie.rating || "PG",
      description: movie.description || "",
      poster_url: movie.poster_url || "",
      logo_url: movie.logo_url || "",
      showtimes: movie.showtimes || [],
      is_active: movie.is_active
    });
    setDialogOpen(true);
  };

  const addShowtime = () => {
    if (newShowtime && !formData.showtimes.includes(newShowtime)) {
      setFormData({ ...formData, showtimes: [...formData.showtimes, newShowtime].sort() });
      setNewShowtime("");
    }
  };

  const removeShowtime = (time) => {
    setFormData({ ...formData, showtimes: formData.showtimes.filter(t => t !== time) });
  };

  const filteredMovies = movies.filter(movie =>
    movie.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    movie.genre.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((left, right) => String(left?.title || '').localeCompare(String(right?.title || ''), undefined, { sensitivity: 'base' }));

  const filteredIds = filteredMovies.map((movie) => movie.id);
  const selectedFilteredIds = filteredIds.filter((id) => selectedIds.has(id));
  const allFilteredSelected = filteredIds.length > 0 && selectedFilteredIds.length === filteredIds.length;
  const someFilteredSelected = selectedFilteredIds.length > 0 && !allFilteredSelected;

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredIds.forEach((id) => next.add(id));
      } else {
        filteredIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleSelectOne = (movieId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(movieId);
      } else {
        next.delete(movieId);
      }
      return next;
    });
  };

  const getBulkActionIds = () => selectedFilteredIds;

  const handleBulkStatus = async (isActive) => {
    const ids = getBulkActionIds();
    if (ids.length === 0) {
      toast.error("No selected movies in the current list");
      return;
    }
    setBulkLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/movies/bulk-status`,
        { ids, is_active: isActive },
        { headers: getAuthHeader() }
      );
      toast.success(
        isActive
          ? `Set ${response.data.modified_count} movie(s) active`
          : `Set ${response.data.modified_count} movie(s) inactive`
      );
      setSelectedIds(new Set());
      fetchMovies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update movie status");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = getBulkActionIds();
    if (ids.length === 0) {
      toast.error("No selected movies in the current list");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${ids.length} selected movie(s)?`)) {
      return;
    }
    setBulkLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/movies/bulk-delete`,
        { ids },
        { headers: getAuthHeader() }
      );
      toast.success(`Deleted ${response.data.deleted_count} movie(s)`);
      setSelectedIds(new Set());
      fetchMovies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete movies");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="movies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Movies</h1>
          <p className="text-muted-foreground mt-1">Manage cinema listings</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gradient rounded-none" data-testid="add-movie-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Movie
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingMovie ? "Edit Movie" : "Add New Movie"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Movie Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="rounded-none"
                      required
                      data-testid="movie-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Genre *</Label>
                    <Select
                      value={formData.genre}
                      onValueChange={(value) => setFormData({ ...formData, genre: value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="movie-genre-select">
                        <SelectValue placeholder="Select genre" />
                      </SelectTrigger>
                      <SelectContent>
                        {MOVIE_GENRES.map(genre => (
                          <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rating</Label>
                    <Select
                      value={formData.rating}
                      onValueChange={(value) => setFormData({ ...formData, rating: value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="movie-rating-select">
                        <SelectValue placeholder="Select rating" />
                      </SelectTrigger>
                      <SelectContent>
                        {MOVIE_RATINGS.map(rating => (
                          <SelectItem key={rating} value={rating}>{rating}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (minutes) *</Label>
                    <Input
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                      className="rounded-none"
                      required
                      data-testid="movie-duration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Poster URL</Label>
                    <Input
                      value={formData.poster_url}
                      onChange={(e) => setFormData({ ...formData, poster_url: e.target.value })}
                      placeholder="https://..."
                      className="rounded-none"
                      data-testid="movie-poster-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      className="rounded-none"
                    />
                    <p className="text-xs text-muted-foreground">Optional: URL to store logo image</p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="rounded-none"
                      rows={3}
                      data-testid="movie-description-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Showtimes</Label>
                    <div className="flex gap-2">
                      <Input
                        type="time"
                        value={newShowtime}
                        onChange={(e) => setNewShowtime(e.target.value)}
                        className="rounded-none flex-1"
                        data-testid="movie-showtime-input"
                      />
                      <Button type="button" onClick={addShowtime} variant="outline" className="rounded-none">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.showtimes.map(time => (
                        <span 
                          key={time} 
                          className="inline-flex items-center gap-1 px-3 py-1 bg-muted text-sm"
                        >
                          {time}
                          <button 
                            type="button" 
                            onClick={() => removeShowtime(time)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="movie-active-switch"
                    />
                    <Label>Active (Now Showing)</Label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-gradient rounded-none" data-testid="movie-submit-btn">
                    {editingMovie ? "Update" : "Create"} Movie
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card className="border-border rounded-none">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search movies by title or genre..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none"
              data-testid="movie-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {selectedIds.size > 0 && (canEdit || canDelete) && (
        <Card className="border-border rounded-none">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm font-medium" data-testid="movies-selected-count">
              {selectedIds.size} selected
              {selectedFilteredIds.length !== selectedIds.size
                ? ` (${selectedFilteredIds.length} in current list)`
                : ""}
            </p>
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    className="rounded-none"
                    disabled={bulkLoading || selectedFilteredIds.length === 0}
                    onClick={() => handleBulkStatus(true)}
                    data-testid="movies-bulk-activate-btn"
                  >
                    Set Active
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-none"
                    disabled={bulkLoading || selectedFilteredIds.length === 0}
                    onClick={() => handleBulkStatus(false)}
                    data-testid="movies-bulk-deactivate-btn"
                  >
                    Set Inactive
                  </Button>
                </>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  disabled={bulkLoading || selectedFilteredIds.length === 0}
                  onClick={handleBulkDelete}
                  data-testid="movies-bulk-delete-btn"
                >
                  Delete selected
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border-border rounded-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse text-muted-foreground">Loading movies...</div>
            </div>
          ) : filteredMovies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Film className="h-12 w-12 mb-3 opacity-50" />
              <p>No movies found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  {(canEdit || canDelete) && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => toggleSelectAllFiltered(checked === true)}
                        aria-label="Select all movies"
                        data-testid="movies-select-all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="font-semibold">Title</TableHead>
                  <TableHead className="font-semibold">Genre</TableHead>
                  <TableHead className="font-semibold">Duration</TableHead>
                  <TableHead className="font-semibold">Rating</TableHead>
                  <TableHead className="font-semibold">Showtimes</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  {canEdit && <TableHead className="font-semibold text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMovies.map((movie) => (
                  <TableRow key={movie.id} className="table-row" data-testid={`movie-row-${movie.id}`}>
                    {(canEdit || canDelete) && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(movie.id)}
                          onCheckedChange={(checked) => toggleSelectOne(movie.id, checked === true)}
                          aria-label={`Select ${movie.title}`}
                          data-testid={`movie-select-${movie.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{movie.title}</TableCell>
                    <TableCell>{movie.genre}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{movie.duration_minutes} min</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700">
                        {movie.rating}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {movie.showtimes?.slice(0, 3).map(time => (
                          <span key={time} className="text-xs font-mono bg-muted px-2 py-0.5">
                            {time}
                          </span>
                        ))}
                        {movie.showtimes?.length > 3 && (
                          <span className="text-xs text-muted-foreground">+{movie.showtimes.length - 3}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        movie.is_active 
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {movie.is_active ? "Now Showing" : "Coming Soon"}
                      </span>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(movie)}
                            data-testid={`edit-movie-${movie.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(movie.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-movie-${movie.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
